import { getApiErrorCode, getApiErrorFields } from "@/lib/api-error";
import { IntegrationCrmSeliarisErrorCode } from "@shaarp/common";

/**
 * Translation key for every code the API attaches to `data.code`, so a refusal
 * from Seliaris reads as something the user can act on rather than as the
 * generic failure toast.
 *
 * Typed against the code union on purpose: a new code cannot ship without a
 * message.
 */
export const SELIARIS_ERROR_MESSAGE_KEY: Record<
  IntegrationCrmSeliarisErrorCode,
  string
> = {
  SELIARIS_API_KEY_REQUIRED: "team.integrations.seliaris.errors.keyRequired",
  SELIARIS_INVALID_API_KEY: "team.integrations.seliaris.errors.invalidKey",
  SELIARIS_FORBIDDEN: "team.integrations.seliaris.errors.forbidden",
  SELIARIS_ACCOUNT_READ_ONLY: "team.integrations.seliaris.errors.readOnly",
  SELIARIS_UNAVAILABLE: "team.integrations.seliaris.errors.unavailable",
  SELIARIS_RATE_LIMITED: "team.integrations.seliaris.errors.rateLimited",
  SELIARIS_VALIDATION_FAILED: "team.integrations.seliaris.errors.rejected",
  SELIARIS_REQUIRED_CUSTOM_FIELDS:
    "team.integrations.seliaris.errors.requiredCustomFields",
  SELIARIS_NOT_CONNECTED: "team.integrations.seliaris.errors.notConnected",
  SELIARIS_OWNER_NOT_FOUND: "team.integrations.seliaris.errors.ownerNotFound",
};

/**
 * A send the user agreed to make without an owner, which Seliaris still refused
 * over its owner. Told apart from the first refusal: the question it would
 * reopen was just answered, and the record was not sent.
 */
export class SeliarisOwnerStillRefusedError extends Error {
  /** The refusal as the API answered it. */
  readonly refusal: unknown;

  constructor(refusal: unknown) {
    super("Seliaris refused the record sent without an owner");
    this.name = "SeliarisOwnerStillRefusedError";
    this.refusal = refusal;
  }
}

/**
 * The message key for a failed Seliaris call, or null when the failure carries
 * no code the UI knows — in which case callers fall back to the generic toast
 * rather than inventing an explanation.
 *
 * @param error The rejected request.
 */
export const getSeliarisErrorMessageKey = (error: unknown): string | null => {
  if (error instanceof SeliarisOwnerStillRefusedError) {
    return "team.integrations.seliaris.errors.ownerStillRefused";
  }
  const code = getApiErrorCode(error);
  if (!code) return null;
  return (
    SELIARIS_ERROR_MESSAGE_KEY[code as IntegrationCrmSeliarisErrorCode] ?? null
  );
};

/**
 * Names of the Seliaris custom fields a refusal blamed, joined for display, or
 * null when the failure carries none.
 *
 * `SELIARIS_REQUIRED_CUSTOM_FIELDS` is the one message a user cannot act on
 * without them: the field to make optional lives in their Seliaris account and
 * only its slug identifies it.
 *
 * @param error The rejected request.
 */
export const getSeliarisErrorFields = (error: unknown): string | null =>
  getApiErrorFields(error)?.join(", ") ?? null;

/**
 * Whether a send failed only because Seliaris knows no user with the sender's
 * email. The refused write changed nothing: this refusal is answered with a
 * choice — send the record without an owner, or not at all — rather than a
 * toast.
 *
 * @param error The rejected request.
 */
export const isSeliarisOwnerNotFound = (error: unknown): boolean =>
  getApiErrorCode(error) === "SELIARIS_OWNER_NOT_FOUND";
